import { ShiftsLeaveFragment } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

interface Props {
  id: number
  type_name: string
  start_on: string
  finish_on: string | null | undefined
  type_workable: boolean
  half_day: string
  hourly: boolean
  start_time: string
  hours_amount: number
  employee_id: number
  employee_full_name: string
  employee_avatar: string
  approved: boolean
  description: string
  fragment?: ShiftsLeaveFragment
}

class Leave {
  id: number
  typeName: string
  _startOn: moment.Moment
  _finishOn: moment.Moment | null | undefined
  workable: boolean
  halfDay: string
  hourly: boolean
  startTime: string
  hoursAmount: number
  employeeId: number
  employeeFullName: string
  employeeAvatar: string
  approved: boolean
  description: string
  fragment?: ShiftsLeaveFragment

  constructor(props: Props) {
    this.id = props.id
    this.typeName = props.type_name
    this._startOn = stringToDate(props.start_on, 'backend')
    this._finishOn = this.getFinishOn(props.finish_on)
    this.workable = props.type_workable
    this.halfDay = props.half_day
    this.hourly = props.hourly
    this.startTime = props.start_time
    this.hoursAmount = props.hours_amount
    this.employeeId = props.employee_id
    this.employeeFullName = props.employee_full_name
    this.employeeAvatar = props.employee_avatar
    this.approved = props.approved
    this.description = props.description

    this.fragment = props.fragment

    makeObservable(this, {
      hours: computed,
      canHaveBackupShifts: computed,
      getFormattedRange: computed,
    })
  }

  static buildFromGraphQL(
    fragment: ShiftsLeaveFragment,
    employeeAvatar: string,
    employeeFullName: string
  ): Leave {
    return new Leave({
      id: fragment.id,
      type_name: fragment.leaveTypeName || '',
      start_on: fragment.startOn,
      finish_on: fragment.finishOn,
      type_workable: !!fragment.leaveTypeWorkable,
      half_day: fragment.halfDay || '',
      hourly: !!fragment.hourly,
      start_time: fragment.startTime || '',
      hours_amount: fragment.hoursAmountInCents || 0,
      description: fragment.description || '',
      // FIXME: Improve this to remove employeeId from here.
      employee_id: fragment.employeeId,
      employee_full_name: employeeFullName,
      employee_avatar: employeeAvatar,
      approved: !!fragment.approved,
      fragment: fragment,
    })
  }

  isDuring(date: moment.Moment): boolean {
    // If you're a bit Oo because of the signature of this method call:
    // Sometimes, the leave does not have an end date (sick leave).
    if (!this._finishOn) {
      return date.isSameOrAfter(this._startOn)
    }

    // Check that `date` is between these other 2 dates, at a scale of days and
    // including both the starting and ending date (on moment `[]` means to do
    // an inclusive check)
    return date.isBetween(this._startOn, this._finishOn, 'day', '[]')
  }

  duration(): number {
    const finishOn = this._finishOn ? this._finishOn : this.getStartOn()

    return finishOn.diff(this.getStartOn(), 'days') + 1
  }

  getFinishOn(finishOn: string | null | undefined): moment.Moment | null | undefined {
    if (finishOn) return stringToDate(finishOn, 'backend')
  }

  getStartOn(): moment.Moment {
    return this._startOn
  }

  get hours(): number {
    return this.hoursAmount / 100.0
  }

  get canHaveBackupShifts(): boolean {
    return !this.workable && !this.hourly && !this.halfDay && this.approved
  }

  get getFormattedRange(): string {
    const formattedStartOn = this._startOn.format('D MMM, YYYY')
    const formattedFinishOn = this._finishOn ? this._finishOn.format('D MMM, YYYY') : ''

    const singleDay = formattedStartOn === formattedFinishOn

    if (!this._finishOn || singleDay) return formattedStartOn
    return `${formattedStartOn} - ${formattedFinishOn}`
  }
}

export default Leave
